import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { NgxImageCompressService } from 'ngx-image-compress';
import { ViewerModalComponent } from 'ngx-ionic-image-viewer';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Subscription, of } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';
import { PartsRating, SeveritySelected, SeveritySelectedCurrent } from 'src/app/helpers';
import { editableRole } from 'src/app/helpers/enums/role-type.enum';
import { AuthService } from 'src/app/services/auth/auth.service';
import { EstimatesService } from 'src/app/services/estimates/estimates.service';
import { LocaleService } from 'src/app/services/locale/locale.service';
import { RatingService } from 'src/app/services/rating/rating.service';
import { SharedService } from 'src/app/services/shared/shared.service';
import { UserService } from 'src/app/services/user-service/user.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-truck-double-cabin',
  templateUrl: './truck-double-cabin.component.html',
  styleUrls: ['./truck-double-cabin.component.scss']
})
export class TruckDoubleCabinComponent implements OnInit {
  public role: string = null;
  public lang: string = null;
  public partConditionLoaded: boolean = false;
  public conditionSubmitting: boolean = false;
  public submitted: boolean = false;
  public conditionForm: UntypedFormGroup = null;
  public vehicleId: string = '';

  public filesOld: object = {
    fileOld1: null,
    fileOld2: null
  };

  public filesNew: object = {
    fileNew1: null,
    fileNew2: null
  };

  public imageOld: object = {
    beforeImg1: null,
    beforeImg2: null
  };

  public imageNew: object = {
    afterImg1: null,
    afterImg2: null
  };

  public rating: string | number = null;
  public overallExtRating: number = null;
  public overallExtLabourCost: number = null;
  public overallExtPartCost: number = null;
  public overallExtRepairCost: number = null;
  public vinInfo: Object = {};
  public parts: Object = {};

  public selectedSeverity: SeveritySelected = {
    exterior: {
      state: [],
      overall: {
        rating: null,
        labour_cost: null,
        part_cost: null,
        repair_cost: null
      },
      endCorrection: {}
    },
    interior: {
      state: [],
      overall: {
        rating: null,
        labour_cost: null,
        part_cost: null,
        repair_cost: null
      },
      endCorrection: {}
    },
    mechanics: {
      state: [],
      overall: {
        rating: null,
        labour_cost: null,
        part_cost: null,
        repair_cost: null
      },
      endCorrection: {}
    },
    frame: {
      state: [],
      overall: {
        rating: null,
        labour_cost: null,
        part_cost: null,
        repair_cost: null
      },
      endCorrection: {}
    },
    major_options: {
      state: [],
      overall: {
        rating: null,
        labour_cost: null,
        part_cost: null,
        repair_cost: null
      },
      endCorrection: {}
    }
  };
  public allParts: any = null;
  public partSeverity: Object = {};
  public partCondition: Object[] = [];
  @Input() data: Object[] = [];
  public modalRef: BsModalRef;
  public modalTitle: string = null;
  @ViewChild('condition', { static: false }) condition: TemplateRef<any>;
  public subscriptions: Subscription[] = [];
  public vehicleStatus: string = '';
  public editableRole: string[] = [];
  public qcConditions: object[] = [];
  public qcOptions: object[] = [];
  public qcForm: FormGroup;
  public toggle: boolean = false;
  defaultRating: string = '5.0';

  constructor(
    public formBuilder: UntypedFormBuilder,
    public modalService: BsModalService,
    public sharedService: SharedService,
    public ratingService: RatingService,
    public userService: UserService,
    public localeServive: LocaleService,
    public authService: AuthService,
    public toastr: ToastrService,
    public estimateService: EstimatesService,
    private imageCompress: NgxImageCompressService,
    private modalController: ModalController
  ) {
    this.editableRole = editableRole;
    this.conditionForm = this.formBuilder.group({
      parts_conditions_id: [''],
      vpcs_severity: ['', [Validators.required]],
      vpcs_s_dec: [''],
      vpcs_lb_cost: [''],
      vpcs_prt_cost: [''],
      vpcs_rp_cost: [''],
      vpcs_old_image1: [''],
      vpcs_old_image2: [''],
      vpcs_new_image1: [''],
      vpcs_new_image2: ['']
    });

    this.authUser();
  }

  ngOnInit(): void {
    // START => getting report status of a vehicle from localStorage
    const vehicleStatus = JSON.parse(localStorage.getItem('vehicleStatus'));
    this.vehicleStatus = vehicleStatus ? vehicleStatus : '';

    if (vehicleStatus === true && !editableRole.includes(this.role)) {
      this.conditionForm.disable();
    }
    if (this.role === 'qc') {
      this.conditionForm.get('vpcs_severity').enable();
      this.conditionForm.get('vpcs_s_dec').enable();
      this.conditionForm.get('vpcs_new_image1').enable();
      this.conditionForm.get('vpcs_new_image2').enable();
    }
    // END

    if (this.data) this.getState();

    const infoSub = this.sharedService.vinInformation.subscribe((res) => {
      if (res) {
        this.vinInfo = res;
      }
      if (res && res?.['is_salvage']) {
        setTimeout(() => {
          this.defaultRating = '1.0';
        }, 100);
      }
    });

    const localeSub = this.localeServive.localeSubject.subscribe((res) => {
      if (res) {
        this.lang = res;
      }
    });

    this.subscriptions.push(infoSub, localeSub);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  get formData() {
    return this.conditionForm.controls;
  }

  authUser() {
    this.authService.user.subscribe((res) => {
      if (res && Object.keys(res).length) {
        let details: Object =
          res['user'] && res['user']['user_data'] ? res['user']['user_data'] : null;

        this.role = details['role'] || 'admin';

        switch (this.role) {
          case 'evaluator':
            this.conditionForm.get('vpcs_lb_cost').clearValidators();
            this.conditionForm.get('vpcs_prt_cost').clearValidators();
            this.conditionForm.get('vpcs_rp_cost').clearValidators();
            this.conditionForm.get('vpcs_s_dec').clearValidators();
            break;
          case 'estimator':
            this.conditionForm.get('parts_conditions_id').disable();
            this.conditionForm.get('parts_conditions_id').clearValidators();
            this.conditionForm.get('vpcs_severity').clearValidators();
            this.conditionForm.get('vpcs_lb_cost').clearValidators();
            this.conditionForm.get('vpcs_prt_cost').clearValidators();
            this.conditionForm.get('vpcs_rp_cost').clearValidators();

            break;
          case 'reconditioning_manager':
            // this.conditionForm.get('parts_conditions_id').disable();
            this.conditionForm.get('parts_conditions_id').clearValidators();
            this.conditionForm.get('vpcs_severity').clearValidators();
            this.conditionForm.get('vpcs_lb_cost').clearValidators();
            this.conditionForm.get('vpcs_prt_cost').clearValidators();
            this.conditionForm.get('vpcs_rp_cost').clearValidators();
            break;
          case 'qc':
            this.conditionForm.get('vpcs_lb_cost').clearValidators();
            this.conditionForm.get('vpcs_prt_cost').clearValidators();
            this.conditionForm.get('vpcs_rp_cost').clearValidators();

            this.conditionForm.get('parts_conditions_id').disable();
            this.conditionForm.get('parts_conditions_id').clearValidators();
            // this.conditionForm.get('vpcs_s_dec').clearValidators();
            break;
          case 'purchasemanager':
            this.conditionForm.get('parts_conditions_id').disable();
            this.conditionForm.get('parts_conditions_id').clearValidators();
            break;
        }

        this.conditionForm.updateValueAndValidity();
      }
    });
  }

  // Getting state data
  getState() {
    this.vehicleId = this.sharedService.getVehicleId;
    let data: object = { vehicle_id: this.vehicleId };

    this.ratingService.getRatingStates(data).subscribe(
      (res) => {
        if (res) {
          let success: boolean = res['success'];
          let data: object = res['data'];

          if (success && Object.keys(data).length) {
            const states: SeveritySelected = data['ratingStates']
              ? JSON.parse(data['ratingStates']['rating_states'])
              : null;

            if (states && states.exterior) {
              states.exterior.state = states.exterior.state.map((state) => {
                const matchingReview = this.data.find(
                  (review) => review?.['parts_condition_id'] === state.part_id
                );

                return {
                  ...state,
                  qc_review_status: matchingReview?.['qc_review_status'] ?? null
                };
              });
            }

            this.sharedService.setSeverity = states;
            this.managePartData(this.data, !!states?.exterior?.state?.length);
          }
        }
      },
      (error: HttpErrorResponse) => {}
    );
  }

  managePartData(data: object[], hasRatingStates?: boolean) {
    let severityData: PartsRating[] = data.map((item: object) => {
      if (item['part_condition']) {
        let partName: string = item['part_condition']['parts_condition_name']
          .replace(/  +/g, ' ')
          .replace(/[\s\/]/g, '_')
          .toLowerCase();
        let partId: string = item['parts_condition_id'];
        this.parts[partName] = partId;

        let Obj = {
          part_id: item['parts_condition_id'],
          part_name: item['part_condition']['parts_condition_name'],
          severity: {
            1: item['sl5'],
            2: item['sl4'],
            3: item['sl3'],
            4: item['sl2'],
            5: item['sl1']
          },
          final_severity: this.vinInfo?.['is_salvage'] ? item['sl5'] : item['sl1'],
          rating_id: item['rating_id'],
          labour_cost: 0,
          part_cost: 0,
          repair_cost: 0
        };

        return Obj;
      }
    });

    severityData = severityData.filter((part: PartsRating) => !!part);
    this.allParts = JSON.stringify(severityData); // To solve a weird bug of value changes when assigning to global varibale.

    const severitySub = this.sharedService.severity.subscribe((res: SeveritySelected) => {
      if (res) {
        this.selectedSeverity = res;
      }
    });

    this.subscriptions.push(severitySub);

    if (this.selectedSeverity.exterior.state.length) {
      this.sharedService.setSeverity = this.selectedSeverity;
    } else {
      this.selectedSeverity.exterior.state = severityData.map((item: PartsRating) => {
        let selectedSeverityLevel: number = null;

        for (let severity in item.severity) {
          if (item.severity[severity] === item.final_severity) {
            selectedSeverityLevel = +severity;
          }
        }

        return {
          severity: item.final_severity,
          severity_level:
            this.vinInfo?.['is_salvage'] && !hasRatingStates ? 1 : selectedSeverityLevel,
          part_id: item.part_id,
          labour_cost: +item.labour_cost,
          part_cost: +item.part_cost,
          repair_cost: +item.repair_cost
        };
      });
    }

    setTimeout(() => {
      let allSelectedSeverity: any[] = [];
      this.selectedSeverity.exterior.state.forEach((part: SeveritySelectedCurrent) => {
        allSelectedSeverity.push(+part.severity);

        this.overallExtLabourCost += +part.labour_cost;
        this.overallExtPartCost += +part.part_cost;
        this.overallExtRepairCost += +part.repair_cost;
        // this.severityChange(+part.severity_level, part.part_id);
        this.severityChange(+part.severity_level, part.part_id, part?.['qc_review_status']);
      });

      if (allSelectedSeverity.length) {
        this.overallExtRating = allSelectedSeverity.reduce(
          (prev, curr) => +Number.parseFloat(prev + curr).toFixed(2)
        );
      }

      this.selectedSeverity.exterior.overall.rating = this.overallExtRating;
      this.selectedSeverity.exterior.overall.labour_cost = this.overallExtLabourCost;
      this.selectedSeverity.exterior.overall.part_cost = this.overallExtPartCost;
      this.selectedSeverity.exterior.overall.repair_cost = this.overallExtRepairCost;
      this.sharedService.setSeverity = this.selectedSeverity;
    }, 0);
  }

  severityChange(value: number, partId: string, qc_review_status?) {
    let partElements: HTMLElement[] = Array.from(document.querySelectorAll('.partGrp'));
    this.rating = value.toFixed(1);

    partElements.forEach((elem: HTMLElement, index: number) => {
      let partIdData: string = elem.id;

      if (partIdData === partId) {
        elem.setAttribute('class', '');

        if ([0, 1].includes(qc_review_status)) {
          const tspanEls = elem.querySelectorAll<SVGTextElement>('text tspan');
          const targetNames = ['Logo', 'RHT Stop lamp'];

          const tspanEl = targetNames.includes(elem.getAttribute('data-name') || '')
            ? tspanEls[1]
            : tspanEls[0];

          if (tspanEl) {
            const existingText = tspanEl.textContent?.trim() || '';
            const prefix = qc_review_status === 1 ? '*' : '-';
            tspanEl.textContent = `${prefix}\u00A0${existingText}\u00A0`;
          }
        }

        if (value >= 5) {
          elem.classList.add('partGrp', 'excellent');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 4.5 && value !== 5) {
          elem.classList.add('partGrp', 'good');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 4 && value <= 4.4) {
          elem.classList.add('partGrp', 'v-fair');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 3.5 && value <= 3.999) {
          elem.classList.add('partGrp', 'fair');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 3 && value <= 3.4) {
          elem.classList.add('partGrp', 'avg');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 2.5 && value <= 2.999) {
          elem.classList.add('partGrp', 'b-avg');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 2 && value <= 2.4) {
          elem.classList.add('partGrp', 'poor');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 1.5 && value <= 1.999) {
          elem.classList.add('partGrp', 'v-poor');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        } else if (value >= 1 && value <= 1.4) {
          elem.classList.add('partGrp', 'junk');
          elem.querySelector<any>('.score').innerHTML = this.rating;
        }
      }
    });
  }

  target: any = null;
  partId: string = null;
  filteredPartIndex: number = null;

  extraFilteredPartIndex: string | undefined = undefined;

  openCondition(evt: Event) {
    let dataTitle: string = evt.currentTarget['dataset'].name;
    let partId: string = evt.currentTarget['id'];

    this.modalTitle = dataTitle;
    this.partId = partId;

    if (['qc', 'admin'].includes(this.role)) {
      this.fetchQcStatus();
    }
    this.extraFilteredPartIndex = partId;
    this.target = evt.currentTarget;
    this.conditionForm.reset();

    for (let key in this.filesOld) {
      this.filesOld[key] = '';
    }

    for (let key in this.filesNew) {
      this.filesNew[key] = '';
    }

    for (let key in this.imageOld) {
      this.imageOld[key] = '';
    }

    for (let key in this.imageNew) {
      this.imageNew[key] = '';
    }

    this.getConditions(partId).then((res) => {
      setTimeout(() => {
        this.getPartsData(this.partId);

        this.modalRef = this.modalService.show(
          this.condition,
          Object.assign({}, { class: 'modal-lg optionsModal conditionModal' })
        );
      }, 0);
    });
  }

  getPartsData(partId: string) {
    let data: object = {
      vehicle_id: this.sharedService.getVehicleId,
      parent_part_id: partId
    };

    this.ratingService.getRatingPartsData(data).subscribe(
      (res) => {
        if (res) {
          if (['qc', 'admin'].includes(this.role)) {
            this.toggle = res?.['data']?.['partsCondition']?.['qc_review_status'] === 1;
          }

          let success: boolean = res['success'];
          let data: object = res['data'];

          if (success && Object.keys(data).length) {
            let partData: object = data['partsCondition'];

            if (partData) {
              partData['vpcs_old_image'] = partData['vpcs_old_image']
                ? JSON.parse(partData['vpcs_old_image'])
                : null;
              partData['vpcs_new_image'] = partData['vpcs_new_image']
                ? JSON.parse(partData['vpcs_new_image'])
                : null;

              if (partData['vpcs_old_image'] && partData['vpcs_old_image'].length) {
                for (let [key, value] of Object.entries(partData['vpcs_old_image'])) {
                  this.imageOld[`beforeImg${+key + 1}`] = value[+key + 1]
                    ? `${environment.base_path}storage/partsConditions/${value[+key + 1]}`
                    : null;
                }
              }

              if (partData['vpcs_new_image'] && partData['vpcs_new_image'].length) {
                for (let [key, value] of Object.entries(partData['vpcs_new_image'])) {
                  this.imageNew[`afterImg${+key + 1}`] = value[+key + 1]
                    ? `${environment.base_path}storage/partsConditions/${value[+key + 1]}`
                    : null;
                }
              }

              let conditionsSelected: string[] = JSON.parse(partData['vpcs_conditions']);
              this.conditionForm.get('parts_conditions_id').setValue(conditionsSelected || '');
              this.conditionForm.get('vpcs_severity').setValue(partData['vpcs_severity'] || '');
              this.conditionForm.get('vpcs_s_dec').setValue(partData['vpcs_s_dec'] || '');
              this.conditionForm.get('vpcs_lb_cost').setValue(partData['vpcs_lb_cost'] || '');
              this.conditionForm.get('vpcs_prt_cost').setValue(partData['vpcs_prt_cost'] || '');
              this.conditionForm.get('vpcs_rp_cost').setValue(partData['vpcs_rp_cost'] || '');
              this.conditionForm.get('vpcs_old_image1').setValue('');
              this.conditionForm.get('vpcs_old_image2').setValue('');
              this.conditionForm.get('vpcs_new_image1').setValue('');
              this.conditionForm.get('vpcs_new_image2').setValue('');
              this.conditionForm.updateValueAndValidity();
            } else {
              for (let key in this.imageOld) {
                this.imageOld[key] = '';
              }

              for (let key in this.imageNew) {
                this.imageNew[key] = '';
              }

              this.conditionForm.get('parts_conditions_id').setValue('');
              this.conditionForm.get('vpcs_severity').setValue('');
              this.conditionForm.get('vpcs_s_dec').setValue('');
              this.conditionForm.get('vpcs_lb_cost').setValue('');
              this.conditionForm.get('vpcs_prt_cost').setValue('');
              this.conditionForm.get('vpcs_rp_cost').setValue('');
              this.conditionForm.get('vpcs_old_image1').setValue('');
              this.conditionForm.get('vpcs_old_image2').setValue('');
              this.conditionForm.get('vpcs_new_image1').setValue('');
              this.conditionForm.get('vpcs_new_image2').setValue('');
              this.conditionForm.updateValueAndValidity();
            }
          }
        }
      },
      (error: HttpErrorResponse) => {}
    );
  }

  getConditions(partId: string) {
    const data: MainParts = {
      body_type_id: this.vinInfo['body_type_id'].id,
      parts_condition_type: 'exterior',
      parent_id: partId,
      vehicle_id: this.sharedService.getVehicleId
    };
    let allParts = JSON.parse(this.allParts);
    this.filteredPartIndex = allParts.findIndex((part: PartsRating) => part.part_id === partId);

    this.partConditionLoaded = true;

    return new Promise((resolve, reject) => {
      const conditionSub = this.ratingService.getPartsCondition(data).subscribe(
        (res) => {
          let success: boolean = res['success'];
          let data: Object = res['data'];

          if (success) {
            this.partCondition = data['child_parts_conditions'];
            this.partSeverity = {
              ...allParts[this.filteredPartIndex].severity
            }; // Taking filtered severity.
            this.partConditionLoaded = false;
            resolve(this.partCondition);
          } else {
            reject(data);
            this.partConditionLoaded = false;
          }
        },
        (error) => {
          reject(error);
          this.partConditionLoaded = false;
        }
      );
      this.subscriptions.push(conditionSub);
    });
  }

  getFile(evt: Event, type: string, order: number) {
    let reader: FileReader = new FileReader();

    if (type === 'old') {
      let target: any = evt.target;
      let fileObj: File = target['files'][0];
      let fileFormatCheck: boolean =
        fileObj.type == 'image/png' ||
        fileObj.type == 'image/jpg' ||
        fileObj.type == 'image/jpeg' ||
        fileObj.type == 'image/webp';

      if (fileFormatCheck) {
        // this.fileOld = fileObj;
        reader.readAsDataURL(fileObj);
        reader.onload = (ev) => {
          for (let index = 1; index <= Object.keys(this.filesOld).length; index++) {
            if (index === order) {
              const image: any = ev.target.result;
              this.imageCompression(image, fileObj.name, type, order);
            }
          }
        };
      } else return false;
    } else if (type === 'new') {
      let target: any = evt.target;
      let fileObj: File = target['files'][0];
      let fileFormatCheck: boolean =
        fileObj.type == 'image/png' ||
        fileObj.type == 'image/jpg' ||
        fileObj.type == 'image/jpeg' ||
        fileObj.type == 'image/webp';

      if (fileFormatCheck) {
        // this.fileNew = fileObj;
        reader.readAsDataURL(fileObj);
        reader.onload = (ev) => {
          for (let index = 1; index <= Object.keys(this.filesNew).length; index++) {
            if (index === order) {
              const image: any = ev.target.result;
              this.imageCompression(image, fileObj.name, type, order);
            }
          }
        };
      } else return false;
    }

    setTimeout(() => {
      evt.target['value'] = '';
    }, 0);
  }

  imageCompression(base64: string, fileName: string, type: string, order: number) {
    this.imageCompress.compressFile(base64, -1, 60, 50).then((res) => {
      if (type === 'old') {
        for (let index = 1; index <= Object.keys(this.filesOld).length; index++) {
          if (index === order) {
            const imageBlobOld = this.sharedService.dataURItoBlob(res.split(',')[1]);
            this.filesOld[`fileOld${order}`] = new File([imageBlobOld], fileName, {
              type: 'image/jpeg'
            });
            this.imageOld[`beforeImg${order}`] = null;
          }
        }
      } else {
        for (let index = 1; index <= Object.keys(this.filesNew).length; index++) {
          if (index === order) {
            const imageBlobNew = this.sharedService.dataURItoBlob(res.split(',')[1]);
            this.filesNew[`fileNew${order}`] = new File([imageBlobNew], fileName, {
              type: 'image/jpeg'
            });
            this.imageNew[`afterImg${order}`] = null;
          }
        }
      }
    });
  }

  async openViewer(imageURL: string) {
    const modal = await this.modalController.create({
      component: ViewerModalComponent,
      componentProps: {
        src: imageURL,
        scheme: 'dark'
      },
      cssClass: 'ion-img-viewer',
      keyboardClose: true,
      showBackdrop: true
    });

    return await modal.present();
  }

  removeImage(image: string | File, type: string, order: number) {
    if (type === 'old') {
      setTimeout(() => {
        // API call to reset
        if (typeof image === 'string') {
          for (let index = 1; index <= Object.keys(this.filesOld).length; index++) {
            if (index === order) {
              this.imageDeleteReq(image);
              this.imageOld[`beforeImg${order}`] = null;
            }
          }
        } else {
          for (let index = 1; index <= Object.keys(this.filesOld).length; index++) {
            if (index === order) {
              this.filesOld[`fileOld${order}`] = null;
            }
          }
        }
      }, 0);
    } else {
      setTimeout(() => {
        // API call to reset
        if (typeof image === 'string') {
          for (let index = 1; index <= Object.keys(this.filesNew).length; index++) {
            if (index === order) {
              this.imageDeleteReq(image);
              this.imageNew[`afterImg${order}`] = null;
            }
          }
        } else {
          for (let index = 1; index <= Object.keys(this.filesNew).length; index++) {
            if (index === order) {
              this.filesNew[`fileNew${order}`] = null;
            }
          }
        }
      }, 0);
    }
  }

  imageDeleteReq(imageName: string) {
    imageName = imageName.replace('/partsConditions/', ' ');
    imageName = imageName.slice(imageName.indexOf(' ')).trim();

    const data: object = {
      vehicle_id: this.sharedService.getVehicleId,
      parent_part_id: this.partId,
      image: imageName
    };

    this.ratingService.deleteRatingImage(data).subscribe(
      (res) => {},
      (error: HttpErrorResponse) => {}
    );
  }

  saveConditionState() {
    let conditionFormData: FormData = new FormData();
    let allParts: PartsRating[] = JSON.parse(this.allParts);

    // const extraFilteredPartIndex = this.selectedSeverity.exterior.state.findIndex(
    //   (el) => el.part_id === this.extraFilteredPartIndex
    // );

    let costOfRepair: any =
      +this.conditionForm.get('vpcs_lb_cost').value +
        +this.conditionForm.get('vpcs_prt_cost').value || '';
    this.conditionForm.get('vpcs_rp_cost').setValue(costOfRepair.toString());

    allParts[this.filteredPartIndex].final_severity =
      this.partSeverity[this.conditionForm.get('vpcs_severity').value];
    allParts[this.filteredPartIndex].repair_cost = this.conditionForm.get('vpcs_rp_cost').value;

    // Updating the selected part state data
    if (
      this.selectedSeverity.exterior.state[this.filteredPartIndex].part_id ===
      allParts[this.filteredPartIndex].part_id
    ) {
      this.selectedSeverity.exterior.state[this.filteredPartIndex].severity =
        allParts[this.filteredPartIndex].final_severity;
      this.selectedSeverity.exterior.state[this.filteredPartIndex].severity_level =
        +this.conditionForm.get('vpcs_severity').value;
      this.selectedSeverity.exterior.state[this.filteredPartIndex].labour_cost =
        +this.conditionForm.get('vpcs_lb_cost').value || 0;
      this.selectedSeverity.exterior.state[this.filteredPartIndex].part_cost =
        +this.conditionForm.get('vpcs_prt_cost').value || 0;
      this.selectedSeverity.exterior.state[this.filteredPartIndex].repair_cost =
        +allParts[this.filteredPartIndex].repair_cost;
    }

    console.log('selectedSeverity: after modification\n', this.selectedSeverity);

    this.submitted = true;

    if (this.conditionForm.invalid) {
      return false;
    } else {
      let severityVal: number = +this.conditionForm.get('vpcs_severity').value;

      conditionFormData.set('vehicle_id', this.sharedService.getVehicleId);
      conditionFormData.set('parent_part_id', this.partId);
      conditionFormData.set(
        'parts_conditions_id',
        JSON.stringify(this.conditionForm.get('parts_conditions_id').value)
      );
      conditionFormData.set('vpcs_severity', this.conditionForm.get('vpcs_severity').value);
      conditionFormData.set('vpcs_s_dec', this.conditionForm.get('vpcs_s_dec').value);
      conditionFormData.set('vpcs_lb_cost', this.conditionForm.get('vpcs_lb_cost').value);
      conditionFormData.set('vpcs_prt_cost', this.conditionForm.get('vpcs_prt_cost').value);
      conditionFormData.set('vpcs_rp_cost', this.conditionForm.get('vpcs_rp_cost').value);
      conditionFormData.set('vpcs_rp_cost', this.conditionForm.get('vpcs_rp_cost').value);
      conditionFormData.set('vpcs_old_image1', this.filesOld['fileOld1']);
      conditionFormData.set('vpcs_old_image2', this.filesOld['fileOld2']);
      conditionFormData.set('vpcs_new_image1', this.filesNew['fileNew1']);
      conditionFormData.set('vpcs_new_image2', this.filesNew['fileNew2']);

      if (['qc', 'admin'].includes(this.role) && this.qcForm) {
        if (!this.qcForm.valid) {
          this.markFormGroupTouched();
          return;
        }

        const formValues = this.qcForm.value;
        const after_condition_id: string[] = [];
        const after_conditions_status: string[] = [];

        this.qcConditions.forEach((condition) => {
          const conditionId = condition?.['condition_id'];
          const selectedValue = formValues[conditionId];
          after_condition_id.push(conditionId);
          after_conditions_status.push(selectedValue || '');
        });

        after_condition_id.forEach((id, index) => {
          conditionFormData.append(`after_condition_id[${index}]`, id);
        });

        after_conditions_status.forEach((id, index) => {
          conditionFormData.append(`after_conditions_status[${index}]`, id);
        });

        conditionFormData.append('qc_review_status', this.toggle ? '1' : '0');
      }

      this.conditionSubmitting = true;
      this.ratingService
        .setPartsCondition(conditionFormData)
        .pipe(
          switchMap((res) => {
            if (!res['success']) {
              this.toastr.error('Something went wrong', 'Failed');
              return EMPTY;
            }

            return this.role === 'evaluator'
              ? this.estimateService.updateRevamCount({
                  vehicle_id: this.sharedService.getVehicleId
                })
              : of(null);
          }),
          tap(() => {
            this.overallExtRating = null;
            this.overallExtLabourCost = null;
            this.overallExtPartCost = null;
            this.overallExtRepairCost = null;
            let allSeverityUpdated: any[] = [];
            this.selectedSeverity.exterior.state.forEach((part: SeveritySelectedCurrent) => {
              allSeverityUpdated.push(+part.severity);
              this.overallExtLabourCost += +part.labour_cost;
              this.overallExtPartCost += +part.part_cost;
              this.overallExtRepairCost += +part.repair_cost;
            });

            this.overallExtRating = allSeverityUpdated.reduce((prev, curr) => {
              return +Number.parseFloat(prev + curr).toFixed(2);
            });

            this.selectedSeverity.exterior.overall.rating = this.overallExtRating;
            this.selectedSeverity.exterior.overall.labour_cost = this.overallExtLabourCost;
            this.selectedSeverity.exterior.overall.part_cost = this.overallExtPartCost;
            this.selectedSeverity.exterior.overall.repair_cost = this.overallExtRepairCost;

            this.saveStateData(this.selectedSeverity);
            this.sharedService.setSeverity = this.selectedSeverity;

            for (let severity in this.partSeverity) {
              if (severity == this.conditionForm.get('vpcs_severity').value) {
                this.severityChange(+severity, this.partId);
                this.modalRef.hide();

                for (let key in this.filesOld) {
                  this.filesOld[key] = null;
                }

                for (let key in this.filesNew) {
                  this.filesNew[key] = null;
                }

                this.conditionForm.get('parts_conditions_id').setValue('');
                this.conditionForm.get('vpcs_severity').setValue('');
                this.conditionForm.get('vpcs_s_dec').setValue('');
                this.conditionForm.get('vpcs_lb_cost').setValue('');
                this.conditionForm.get('vpcs_prt_cost').setValue('');
                this.conditionForm.get('vpcs_rp_cost').setValue('');
                this.conditionForm.updateValueAndValidity();
                this.submitted = false;
              }
            }
            this.toastr.success('Rating added successfully', 'Success');
          }),
          finalize(() => {
            this.conditionSubmitting = false;
          })
        )
        .subscribe({
          error: () => {
            this.toastr.error('Something went wrong', 'Failed');
          }
        });
    }
  }

  // Saving rating state data to DB/locally
  saveStateData(state: SeveritySelected) {
    let data: object = {
      rating_states_id: null,
      vehicle_id: this.sharedService.getVehicleId,
      rating_states: JSON.stringify(state)
    };

    this.ratingService.setRatingStates(data).subscribe(
      (res) => {
        if (res) {
        }
      },
      (error: HttpErrorResponse) => {}
    );
  }

  receiveData(data: object) {
    const formControls = this.conditionForm.controls;
    const updateValues: any = {};

    Object.keys(data).forEach((key) => {
      if (formControls[key]) {
        updateValues[key] = data[key];
      }
    });

    this.conditionForm.patchValue(updateValues);

    if (data?.['vpcs_conditions']) {
      this.conditionForm.get('parts_conditions_id').setValue(JSON.parse(data['vpcs_conditions']));
    }

    if (data?.['vpcs_old_image']) {
      const parsedData: object[] = JSON.parse(data['vpcs_old_image']);

      parsedData.forEach((data, index) => {
        if (data?.[index + 1]) {
          this.imageOld[`beforeImg${index + 1}`] = `${
            environment.base_path
          }storage/partsConditions/${data?.[index + 1]}`;
        }
      });
    }

    if (data?.['vpcs_new_image']) {
      const parsedData: object[] = JSON.parse(data['vpcs_new_image']);

      parsedData.forEach((data, index) => {
        if (data?.[index + 1]) {
          this.imageNew[`afterImg${index + 1}`] = `${
            environment.base_path
          }storage/partsConditions/${data?.[index + 1]}`;
        }
      });
    }
  }

  fetchQcConditions() {
    this.ratingService
      .getQcConditions({
        vehicle_id: this.vehicleId,
        parent_part_id: this.partId
      })
      .subscribe({
        next: (res) => {
          this.qcConditions = res;
          this.buildForm();
          this.checkIfQcStatusExists();
        },
        error: (error) => {
          this.qcConditions = [];
        }
      });
  }

  fetchQcStatus() {
    this.ratingService.getQcStatus().subscribe({
      next: (res) => {
        this.qcOptions = res;
        this.fetchQcConditions();
      },
      error: (error) => {
        this.qcOptions = [];
      }
    });
  }

  buildForm() {
    const formControls: { [key: string]: any } = {};

    this.qcConditions.forEach((condition) => {
      formControls[condition?.['condition_id']] = ['', [Validators.required]];
    });

    this.qcForm = this.formBuilder.group(formControls);
  }

  markFormGroupTouched() {
    Object.keys(this.qcForm.controls).forEach((key) => {
      this.qcForm.get(key)?.markAsTouched();
    });
  }

  hasError(conditionId: string): boolean {
    const control = this.qcForm.get(conditionId);
    return !!(control && control.invalid && control.touched);
  }

  getErrorMessage(conditionId: string): string {
    const control = this.qcForm.get(conditionId);
    if (control?.errors?.['required']) {
      return 'Please select an option';
    }
    return '';
  }

  processFormData(conditionIds: string[], statusValues: string[]) {
    const formData = {
      after_condition_id: conditionIds,
      after_conditions_status: statusValues,
      toggle_status: this.toggle
    };

    return formData;
  }

  checkIfQcStatusExists() {
    const payload = { vehicle_id: this.vehicleId, parent_part_id: this.partId };

    this.ratingService.checkIfQcStatusExists(payload).subscribe({
      next: (res) => {
        res.forEach((item) => {
          if (this.qcForm.contains(item.condition_id)) {
            this.qcForm.get(item.condition_id)?.setValue(item.review_status_id);
          }
        });
      }
    });
  }
}
interface MainParts {
  body_type_id: string;
  parts_condition_type: string;
  parent_id?: string;
  vehicle_id?: string;
}
